/**
 * ModeTogglePopover — compact, 1-click-accessible mode switch.
 * Opened by clicking the mode pill in the header bar.
 * Kill switch (instant SIM) is always visible; LIVE requires password + ack.
 */
import { useState } from 'react';
import * as api from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';

interface Props {
  onClose: () => void;
}

export function ModeTogglePopover({ onClose }: Props) {
  const { me, refresh } = useAuth();
  const toast = useToast();
  const isLive = me?.mode === 'live';
  const [pw, setPw] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  async function killSwitch() {
    setBusy(true);
    try {
      await api.settings.killSwitch();
      await refresh();
      toast.show('Switched to SIM', 'ok');
      onClose();
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function switchMode() {
    const target = isLive ? 'sim' : 'live';
    setBusy(true);
    try {
      await api.settings.changeMode(target, pw, !isLive && ack);
      await refresh();
      toast.show(`Switched to ${target.toUpperCase()}`, target === 'live' ? 'warn' : 'ok');
      onClose();
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute top-full left-0 mt-1 z-50 card p-4 w-72 shadow-lg" data-testid="mode-popover">
        <div className="text-xs font-semibold mb-2">Trading Mode</div>
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-sm font-bold ${isLive ? 'text-bad' : 'text-ok'}`}>
            {isLive ? 'LIVE — real money' : 'SIM — virtual money'}
          </span>
        </div>

        {isLive && (
          <button
            onClick={killSwitch}
            disabled={busy}
            className="btn-danger text-xs w-full mb-3"
            data-testid="kill-switch-btn"
          >
            {busy ? '...' : 'Kill switch (instant SIM)'}
          </button>
        )}

        <div className="border-t border-edge pt-3 space-y-2">
          <div className="text-[11px] text-ink-mute">
            {isLive ? 'Switch back to SIM:' : 'Switch to LIVE (real money):'}
          </div>
          {!isLive && (
            <>
              <input
                type="password"
                className="input w-full text-xs"
                placeholder="Password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoFocus
              />
              <label className="flex items-start gap-2 text-[11px] text-ink-mute">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  className="mt-0.5"
                />
                <span>I acknowledge LIVE mode places real orders that may result in real financial loss.</span>
              </label>
            </>
          )}
          <button
            onClick={switchMode}
            disabled={busy || (!isLive && (!pw || !ack))}
            className={`${isLive ? 'btn' : 'btn-danger'} text-xs w-full`}
            data-testid="mode-switch-btn"
          >
            {busy ? '...' : isLive ? 'Switch to SIM' : 'Switch to LIVE'}
          </button>
        </div>
      </div>
    </>
  );
}
